// packages/web/src/components/Layout/CookieBanner.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Link as MuiLink,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MuiCookieIcon from '@mui/icons-material/Cookie';
import SettingsIcon from '@mui/icons-material/Settings';

// Cookie banner positioned above filter FAB
const CookieBannerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1050,
  backgroundColor: '#ffffff',
  borderTop: '3px solid #c4d600',
  boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.12)',
  padding: theme.spacing(isMobile ? 2 : 3),
  
  // Mobile: Full width with padding
  ...(isMobile && {
    padding: theme.spacing(2),
  }),
}));

const CookieContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  
  // Mobile: Stack vertically
  ...(isMobile && {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  }),
}));

const CookieIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  background: 'linear-gradient(135deg, #84b817 0%, #c4d600 100%)',
  borderRadius: '16px',
  color: 'white',
  flexShrink: 0,
  boxShadow: '0 4px 12px rgba(132, 184, 23, 0.3)',
}));

const CookieText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  flex: 1,
  
  // Mobile: Full width
  ...(isMobile && {
    width: '100%',
  }),
}));

const CookieActions = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  flexShrink: 0,
  
  // Mobile: Full width with stacked buttons
  ...(isMobile && {
    width: '100%',
    flexDirection: 'column',
    gap: theme.spacing(1),
  }),
}));

const AcceptButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontWeight: 700,
  padding: theme.spacing(1.25, 3.5),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
}));

const RejectButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  border: '2px solid #1a1a1a',
  fontWeight: 600,
  padding: theme.spacing(1.25, 3),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontSize: '1rem',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  transition: 'all 0.2s ease',
}));

const SettingsButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#84b817',
  border: '2px solid #84b817',
  fontWeight: 600,
  padding: theme.spacing(1.25, 2.5),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontSize: '1rem',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(132, 184, 23, 0.08)',
    borderColor: '#679614',
    color: '#679614',
  },
  transition: 'all 0.2s ease',
}));

const SettingsPanel = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  padding: theme.spacing(3),
  backgroundColor: '#f9fdf0',
  borderRadius: '12px',
  border: '1px solid #e5e5e5',
}));

const SettingsPanelTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2.5),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const SettingItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  marginBottom: theme.spacing(1.5),
  border: '1px solid #e5e5e5',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ToggleButton = styled(Button)<{ $active?: boolean }>(({ theme, $active }) => ({
  minWidth: 70,
  height: 36,
  borderRadius: '8px',
  fontWeight: 700,
  fontSize: '0.875rem',
  textTransform: 'none',
  backgroundColor: $active ? '#c4d600' : 'transparent',
  color: $active ? '#1a1a1a' : '#666',
  border: $active ? 'none' : '2px solid #e0e0e0',
  boxShadow: $active ? '0 2px 6px rgba(196, 214, 0, 0.2)' : 'none',
  '&:hover': {
    backgroundColor: $active ? '#b3c200' : 'rgba(0, 0, 0, 0.04)',
    borderColor: $active ? 'none' : '#1a1a1a',
  },
  transition: 'all 0.2s ease',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: '#666',
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#1a1a1a',
  },
}));

// Cookie preferences (essential cookies only for tennis scheduling)
interface CookiePreferences {
  essential: boolean;      // Always true - required for authentication, session management
  analytics: boolean;      // Google Analytics for site improvement
  marketing: boolean;      // Marketing/advertising cookies
}

const COOKIE_CONSENT_KEY = 'matchpoint_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'matchpoint_cookie_preferences';

export const CookieBanner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  // Check if user has already given consent
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    }
    
    // Load saved preferences
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        console.warn('Failed to parse cookie preferences:', error);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    
    saveConsent(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    
    saveConsent(essentialOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    
    // Initialize analytics if consented
    if (prefs.analytics) {
      // Initialize Google Analytics here
      console.log('Analytics consent given - initialize GA');
    }
    
    // Handle marketing cookies if consented
    if (prefs.marketing) {
      // Initialize marketing tools here
      console.log('Marketing consent given - initialize marketing tools');
    }
  };

  const handleClose = () => {
    // User dismissed without choosing - default to essential only
    handleRejectAll();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <CookieBannerContainer isMobile={isMobile}>
      <CookieContent isMobile={isMobile}>
        <CookieIconWrapper>
          <MuiCookieIcon sx={{ fontSize: 32 }} />
        </CookieIconWrapper>
        
        <CookieText isMobile={isMobile}>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.75, fontSize: '1.125rem', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
            🍪 We use cookies to enhance your experience
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            We use essential cookies for authentication and site functionality. Optional cookies help us improve your tennis match scheduling experience.{' '}
            <MuiLink 
              href="/privacy-policy" 
              sx={{ 
                color: '#84b817',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': { 
                  textDecoration: 'underline',
                  color: '#679614'
                }
              }}
            >
              Learn more in our Privacy Policy
            </MuiLink>
          </Typography>
          
          {/* Cookie Settings Panel */}
          <Collapse in={showSettings}>
            <SettingsPanel>
              <SettingsPanelTitle>
                Cookie Preferences
              </SettingsPanelTitle>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <SettingItem>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Essential Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Required for authentication, session management, and core functionality
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#84b817', whiteSpace: 'nowrap' }}>
                    Always Active
                  </Typography>
                </SettingItem>
                
                <SettingItem>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Help us understand how you use the site to improve your experience
                    </Typography>
                  </Box>
                  <ToggleButton
                    $active={preferences.analytics}
                    onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                  >
                    {preferences.analytics ? 'ON' : 'OFF'}
                  </ToggleButton>
                </SettingItem>
                
                <SettingItem>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Used to show you relevant tennis-related content and advertisements
                    </Typography>
                  </Box>
                  <ToggleButton
                    $active={preferences.marketing}
                    onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                  >
                    {preferences.marketing ? 'ON' : 'OFF'}
                  </ToggleButton>
                </SettingItem>
              </Box>
            </SettingsPanel>
          </Collapse>
        </CookieText>
        
        <CookieActions isMobile={isMobile}>
          {!showSettings ? (
            <>
              <SettingsButton
                onClick={() => setShowSettings(true)}
                startIcon={<SettingsIcon />}
                sx={{ ...(isMobile && { width: '100%' }) }}
              >
                Settings
              </SettingsButton>
              <RejectButton
                onClick={handleRejectAll}
                sx={{ ...(isMobile && { width: '100%' }) }}
              >
                Essential Only
              </RejectButton>
              <AcceptButton
                onClick={handleAcceptAll}
                sx={{ ...(isMobile && { width: '100%' }) }}
              >
                Accept All
              </AcceptButton>
            </>
          ) : (
            <>
              <RejectButton
                onClick={() => setShowSettings(false)}
                sx={{ ...(isMobile && { width: '100%' }) }}
              >
                Cancel
              </RejectButton>
              <AcceptButton
                onClick={handleSavePreferences}
                sx={{ ...(isMobile && { width: '100%' }) }}
              >
                Save Preferences
              </AcceptButton>
            </>
          )}
          
          <CloseButton
            onClick={handleClose}
            size="small"
            sx={{ 
              ...(isMobile && { alignSelf: 'flex-end', mt: 1 }),
            }}
          >
            <CloseIcon />
          </CloseButton>
        </CookieActions>
      </CookieContent>
    </CookieBannerContainer>
  );
};